import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Loader2, Sparkles, FileText, Send, Save } from "lucide-react";
import { toast } from "sonner";

const FIELD = "mt-1 border-zinc-800 bg-zinc-900";
const SEL = "mt-1 h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 text-sm text-zinc-200";
const EMPTY = { event_id: "", vendor: "", vat_number: "", date: new Date().toISOString().slice(0, 10), amount_ht: "", amount_tva: "", amount_ttc: "", description: "", file_id: "", user_id: "" };

function ReceiptThumb({ fileId }) {
  const [prev, setPrev] = useState(null);
  useEffect(() => {
    let url = "";
    api.get(`/expense-files/${fileId}`, { responseType: "blob" })
      .then(({ data }) => { url = URL.createObjectURL(data); setPrev({ url, pdf: data.type === "application/pdf" }); })
      .catch(() => setPrev(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fileId]);
  if (!prev) return <span className="h-14 w-14 shrink-0 animate-pulse rounded-lg bg-zinc-800" />;
  return (
    <a href={prev.url} target="_blank" rel="noreferrer" data-testid="expense-file-thumb" title="Ouvrir le justificatif" onClick={(e) => e.stopPropagation()}
      className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-zinc-700 bg-zinc-900 transition-transform hover:scale-105">
      {prev.pdf ? <span className="flex flex-col items-center text-[10px] font-semibold text-red-300"><FileText className="h-6 w-6" />PDF</span> : <img src={prev.url} alt="Justificatif" className="h-full w-full object-cover" />}
    </a>
  );
}

function Receipt({ form, setForm, fileName, setFileName }) {
  const [busy, setBusy] = useState("");
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setBusy("upload");
    try {
      const { data } = await api.post("/expense-files", fd);
      setFileName(data.name); setForm((p) => ({ ...p, file_id: data.file_id }));
      setBusy("scan");
      const { data: s } = await api.post(`/expense-files/${data.file_id}/scan`);
      setForm((p) => ({ ...p, ...Object.fromEntries(Object.entries(s).filter(([, v]) => v !== null && v !== "")) }));
      toast.success("Justificatif lu automatiquement — vérifiez les champs");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Erreur d'envoi"); }
    finally { setBusy(""); }
  };
  return (
    <label data-testid="expense-file-drop" className="flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed border-zinc-800 bg-zinc-900/40 p-4 transition-colors hover:border-[#FF6A00]/50">
      {busy === "upload" ? <Loader2 className="h-6 w-6 animate-spin text-[#FF6A00]" /> : form.file_id ? <ReceiptThumb fileId={form.file_id} /> : <Upload className="h-6 w-6 text-[#FF6A00]" />}
      <div className="min-w-0 text-sm">
        <p className="truncate font-medium text-white">{busy === "scan" ? "Lecture automatique en cours…" : form.file_id ? (fileName || "Justificatif joint") : "Déposer la photo ou le PDF de la facture"}</p>
        <p className="flex items-center gap-1 text-xs text-zinc-500">{busy === "scan" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}{form.file_id && !busy ? "Touchez pour remplacer le justificatif" : "Les champs sont remplis automatiquement à partir du justificatif"}</p>
      </div>
      <input data-testid="expense-file-input" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" capture="environment" className="hidden" onChange={onFile} disabled={!!busy} />
    </label>
  );
}

function Amounts({ form, set }) {
  const [auto, setAuto] = useState(!form.amount_ttc);
  const recompute = (k) => (e) => {
    set(k)(e);
    if (!auto) return;
    const next = { ...form, [k]: e.target.value };
    const ht = parseFloat(next.amount_ht) || 0, tva = parseFloat(next.amount_tva) || 0;
    set("amount_ttc")({ target: { value: ht || tva ? (ht + tva).toFixed(2) : "" } });
  };
  return (
    <div className="grid grid-cols-3 gap-3">
      <div><Label className="text-zinc-400">Montant HT €</Label><Input data-testid="expense-ht" type="number" step="0.01" className={FIELD} value={form.amount_ht} onChange={recompute("amount_ht")} /></div>
      <div><Label className="text-zinc-400">TVA €</Label><Input data-testid="expense-tva" type="number" step="0.01" className={FIELD} value={form.amount_tva} onChange={recompute("amount_tva")} /></div>
      <div><Label className="text-zinc-400">Montant TTC € *</Label><Input data-testid="expense-ttc" type="number" step="0.01" className={FIELD} value={form.amount_ttc} onChange={(e) => { setAuto(false); set("amount_ttc")(e); }} /></div>
    </div>
  );
}

export default function ExpenseForm({ open, onOpenChange, editing, meta, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) { setForm(editing ? { ...EMPTY, ...editing, user_id: editing.user_id } : EMPTY); setFileName(editing?.file_id ? "Justificatif joint" : ""); }
  }, [open, editing]);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const save = async (submit) => {
    setSaving(true);
    const payload = { ...form, amount_ht: parseFloat(form.amount_ht) || 0, amount_tva: parseFloat(form.amount_tva) || 0, amount_ttc: parseFloat(form.amount_ttc) || 0, user_id: form.user_id || null };
    try {
      const { data } = editing ? await api.put(`/expenses/${editing.id}`, payload) : await api.post("/expenses", payload);
      if (submit) await api.post(`/expenses/${data.id}/submit`);
      toast.success(submit ? "Note de frais envoyée pour validation" : "Brouillon enregistré");
      onOpenChange(false); onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="expense-form" className="max-h-[90vh] max-w-xl overflow-y-auto border-zinc-800 bg-zinc-950 text-white">
        <DialogHeader><DialogTitle>{editing ? "Modifier la note de frais" : "Nouvelle note de frais"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Receipt form={form} setForm={setForm} fileName={fileName} setFileName={setFileName} />
          {meta.is_admin && !editing && (
            <div><Label className="text-zinc-400">Employé</Label>
              <select data-testid="expense-user" value={form.user_id || ""} onChange={set("user_id")} className={SEL}>
                <option value="">Moi-même</option>
                {meta.members.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
              </select>
            </div>
          )}
          <div><Label className="text-zinc-400">Événement lié</Label>
            <select data-testid="expense-event" value={form.event_id || ""} onChange={set("event_id")} className={SEL}>
              <option value="">Frais généraux / déplacement (sans événement)</option>
              {meta.events.map((ev) => <option key={ev.id} value={ev.id}>{ev.title}{ev.date ? ` · ${ev.date.slice(0, 10)}` : ""}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-zinc-400">Acheté chez *</Label><Input data-testid="expense-vendor" className={FIELD} value={form.vendor} onChange={set("vendor")} /></div>
            <div><Label className="text-zinc-400">Date d'achat</Label><Input data-testid="expense-date" type="date" className={FIELD} value={form.date} onChange={set("date")} /></div>
          </div>
          <div><Label className="text-zinc-400">N° de TVA du fournisseur</Label><Input data-testid="expense-vat" className={FIELD} placeholder="FR00123456789" value={form.vat_number} onChange={set("vat_number")} /></div>
          <Amounts key={open ? (editing?.id || "new") : "closed"} form={form} set={set} />
          <div><Label className="text-zinc-400">Description</Label><Input data-testid="expense-desc" className={FIELD} value={form.description} onChange={set("description")} /></div>
          <div className="flex justify-end gap-2">
            <Button data-testid="expense-save-draft" variant="outline" disabled={saving} onClick={() => save(false)} className="rounded-full border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800"><Save className="mr-1.5 h-4 w-4" />Brouillon</Button>
            <Button data-testid="expense-submit" disabled={saving} onClick={() => save(true)} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Send className="mr-1.5 h-4 w-4" />Envoyer</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
